import React, { type FunctionComponent, useMemo } from 'react'
import { dashPixels } from 'legacy/dash-blocks/foundation/spacing-units/spacingUnits.types'
import { type OrderType } from 'legacy/sections/orders/utils/orders.types'
import { SimpleSelect } from 'legacy/dash-components/simple-select/SimpleSelect'
import { useDashIntl } from 'intl/intl.hooks'
import { type OverridableStyles } from 'legacy/common/utils/styling/styling.types'
import { type TestableProps } from 'legacy/common/utils/testing/testing.types'
import { useHasAccess } from 'legacy/components/AccessControl/useHasAccess'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import AccessControl from 'legacy/components/AccessControl/AccessControl'
import { Permission } from 'legacy/common/types'

interface Props extends OverridableStyles, TestableProps {
  value: OrderType
  onChange: (nextValue: OrderType) => void
}

type OrderOption = {
  value: OrderType
  label: string
}

export const OrderTypeFilter: FunctionComponent<Props> = ({ value, onChange, ...rest }) => {
  const intl = useDashIntl()

  const hasRxMetricsAccess = useHasAccess({ flippers: Flipper.RxPerformanceMetrics })
  const hasRxTlogAccess = useHasAccess({ permissions: Permission.RxTlogs })

  const options = useMemo(() => {
    const orderOptions: OrderOption[] = [
      {
        value: undefined,
        label: intl.formatMessage({ id: 'orderType.all' }),
      },
      {
        value: 'delivery' as const,
        label: intl.formatMessage({ id: 'orderType.delivery' }),
      },
      {
        value: 'pickup' as const,
        label: intl.formatMessage({ id: 'orderType.pickup' }),
      },
    ]

    if (hasRxMetricsAccess || hasRxTlogAccess) {
      orderOptions.push({
        value: 'rx' as const,
        label: intl.formatMessage({ id: 'orderType.rx' }),
      })
    }

    return orderOptions
  }, [intl, hasRxMetricsAccess, hasRxTlogAccess])

  return (
    <SimpleSelect<OrderType>
      value={value}
      options={options}
      onChange={onChange}
      width={dashPixels.PX128}
      {...rest}
    />
  )
}
